// Imports
import { Plugin } from '@ckeditor/ckeditor5-core';
import { FileRepository, FileLoader, UploadResponse } from '@ckeditor/ckeditor5-upload';


// Custom upload adapter
class CustomUploadAdapter {
	// Initialize properties
	private loader: FileLoader;
	private uploadFunction: (file: File | null) => Promise<UploadResponse>;
	
	
	// Assign properties at instantiation
	constructor(loader: FileLoader, uploadFunction: (file: File | null) => Promise<UploadResponse>) {
		this.loader = loader;
		this.uploadFunction = uploadFunction;
	}
	
	
	// Handle upload
	public upload(): Promise<UploadResponse> {
		return this.loader.file.then((file) => this.uploadFunction(file));
	}
	
	
	// Handle aborting
	public abort(): void {
		// Do nothing
	}
}


// Plugin
export default class CustomUpload extends Plugin {
	// The plugins this plugin relies on
	public static get requires(): [typeof FileRepository] {
		return [FileRepository];
	}
	
	
	// The name this plugin can be referred to by
	public static get pluginName(): 'CustomUpload' {
		return 'CustomUpload';
	}
	
	
	// Handle plugin initialization
	public init(): void {
		// Extract the upload function
		//  -> This is nested in `customUpload` due to strange processing of options at the base level
		const { uploadFunction } = this.editor.config.get('customUpload') as {
			uploadFunction: (file: File | null) => Promise<UploadResponse>;
		};
		
		
		// Handle missing or unexpected type for upload function
		if (typeof uploadFunction !== 'function') {
			throw new Error('No `uploadFunction` was provided');
		}
		
		
		// Replace default upload adapter with our custom adapter
		this.editor.plugins.get(FileRepository).createUploadAdapter = (loader): CustomUploadAdapter => {
			return new CustomUploadAdapter(loader, uploadFunction);
		};
	}
}
